import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * Centred modal, for confirmations and short forms.
 *
 * Sized with `min()` rather than a breakpoint so it is 520px on a laptop and
 * near-full-width on a phone with no second rule to keep in sync. Height is
 * capped in `dvh`, not `vh` — on mobile Safari `vh` is the *largest* viewport,
 * which puts the footer buttons under the browser chrome.
 */
export function Dialog(p: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  /**
   * Overrides the default 520px width, in the same `min()` shape so a wider
   * dialog still fits a phone. For content that is read rather than filled in.
   */
  widthClass?: string
}) {
  return (
    <DialogPrimitive.Root open={p.open} onOpenChange={p.onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]',
            'transition-opacity duration-150 ease-out starting:opacity-0'
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed top-1/2 left-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 flex-col',
            'max-h-[calc(100dvh-3rem)]',
            p.widthClass ?? 'w-[min(520px,calc(100vw-1.5rem))]',
            'rounded-xl border border-line bg-s2 shadow-e3',
            'transition-[opacity,transform] duration-150 ease-out',
            'starting:scale-[0.97] starting:opacity-0'
          )}
        >
          <header className="flex flex-none items-start gap-3 px-5 pt-4 pb-2">
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="font-display text-[16px] leading-[22px] font-semibold text-fg">
                {p.title}
              </DialogPrimitive.Title>
              {p.description !== undefined && p.description !== null ? (
                <DialogPrimitive.Description className="mt-1.5 text-[13px] leading-[1.6] text-muted">
                  {p.description}
                </DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">Dialog</DialogPrimitive.Description>
              )}
            </div>

            <Tooltip content="Close" side="left">
              <DialogPrimitive.Close
                aria-label="Close dialog"
                className={cn(
                  '-mt-1 -mr-2 flex size-8 flex-none items-center justify-center rounded-md text-muted',
                  'transition-colors duration-100 hover:bg-s3 hover:text-fg'
                )}
              >
                <X className="size-[18px]" aria-hidden="true" />
              </DialogPrimitive.Close>
            </Tooltip>
          </header>

          {p.children ? (
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-2 text-[13.5px] text-fg">
              {p.children}
            </div>
          ) : null}

          <footer
            className={cn(
              'flex flex-none items-center justify-end gap-2 px-5 pt-3 pb-4',
              // Stacked and thumb-sized on a phone, reversed so the primary
              // action stays closest to the thumb.
              'max-sm:flex-col-reverse max-sm:[&>button]:h-11 max-sm:[&>button]:w-full max-sm:[&>button]:text-[14px]',
              p.footer ? 'border-t border-line' : null
            )}
          >
            {p.footer ?? (
              <DialogPrimitive.Close
                className={cn(
                  'inline-flex h-8 items-center rounded-md border border-line bg-s3 px-3',
                  'text-[13px] font-medium text-fg transition-colors duration-100',
                  'hover:border-line-strong hover:bg-s1',
                  'max-sm:h-11 max-sm:w-full max-sm:justify-center'
                )}
              >
                Close
              </DialogPrimitive.Close>
            )}
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
